import React, { useState } from 'react';
import { FaGavel, FaGlobeAmericas, FaHandshake, FaLaptopCode, FaUsers } from 'react-icons/fa';
import '../../styles/MinisterialAward.css';

export default function MinisterialAward() {
  const [showAboutModal, setShowAboutModal] = useState(false);
  const categories = [
    {
      icon: <FaGavel />,
      title: 'Digital Policy Architecture',
      desc: 'Honouring transformative legislation and visionary national strategy that reshapes the digital landscape.'
    },
    {
      icon: <FaUsers />,
      title: 'Inclusive Digital Empowerment',
      desc: 'Recognizing bold leadership in bridging the digital divide and empowering every citizen to participate in the digital age.'
    },
    {
      icon: <FaLaptopCode />,
      title: 'Integrated National Transformation',
      desc: 'Celebrating the strategic use of ICT to drive progress across education, health, finance, climate resilience, and governance.'
    },
  ];

  const categoriesBottom = [
    {
      icon: <FaGlobeAmericas />,
      title: 'Regional & Global Digital Influence',
      desc: 'Acknowledging leadership that strengthens Caribbean collaboration and advances alignment with international digital priorities.'
    },
    {
      icon: <FaHandshake />,
      title: 'Digital Investment & Infrastructure Acceleration',
      desc: 'Honouring the mobilization of capital, innovation, and public-private collaboration to accelerate digital economic growth.'
    },
  ];

  return (
    <div className='ministerial-page'>
      {/* Banner / Video Section */}
      <div className='ministerial-banner'>
        <video autoPlay muted loop playsInline>
          <source src='https://canto.org/data/videos/CANTO_Ministerial_Awards_Video.mp4' type='video/mp4' />
        </video>
      </div>

      {/* Header Section Below Video */}
      <div className='ministerial-header'>
        <div className='ministerial-header-title'>
          CANTO Ministerial Awards for ICT Leadership & Digital Transformation
        </div>
        <div className='ministerial-header-subtitle'>
          Celebrating Visionary Ministers Advancing the Caribbean's Digital Future
        </div>
      </div>

      {/* About / Introduction */}
      <div className='ministerial-about'>
        <div className='ministerial-about-label'>About the Awards</div>
        <div className='ministerial-about-text'>
          The CANTO Ministerial Awards for ICT Leadership & Digital Transformation shines a spotlight on visionary Ministers who are boldly advancing the region's digital future. Celebrating exceptional leadership, the awards recognize those turning ambition into action, driving innovation, expanding connectivity, and unlocking opportunities for citizens and businesses alike. More than an accolade, it's a powerful call to action, inspiring governments to place ICT at the heart of sustainable development, inclusive growth, and a thriving digital society.
        </div>
        <button
          onClick={() => setShowAboutModal(true)}
          className='ministerial-about-btn'
        >
          About the Awards
        </button>
      </div>

      {/* Call to Action */}
      <div className='ministerial-cta'>
        <div className='ministerial-cta-title'>A Call to Recognize Exceptional Leadership</div>
        <div className='ministerial-cta-subtitle'>
          "Honouring Caribbean Excellence in Digital Leadership — Be Part of the Legacy."
        </div>
        <a
          href='https://forms.gle/R2ihAtKzeVkZuMEE9'
          target='_blank'
          rel='noopener noreferrer'
          className='ministerial-cta-btn'
        >
          Submit Your Nomination
        </a>
      </div>

      {/* Categories / Areas of Review */}
      <div className='ministerial-categories'>
        <div className='ministerial-categories-label'>Areas of Review</div>
        <div className='ministerial-categories-title'>5 Pillars of Caribbean Digital Excellence</div>
        <div className='ministerial-categories-grid'>
          {categories.map((cat, i) => (
            <div className='ministerial-category-card' key={i}>
              <div className='ministerial-category-icon'>{cat.icon}</div>
              <div className='ministerial-category-title'>{cat.title}</div>
              <div className='ministerial-category-desc'>{cat.desc}</div>
            </div>
          ))}
        </div>
        <div className='ministerial-categories-grid-bottom'>
          {categoriesBottom.map((cat, i) => (
            <div className='ministerial-category-card' key={i}>
              <div className='ministerial-category-icon'>{cat.icon}</div>
              <div className='ministerial-category-title'>{cat.title}</div>
              <div className='ministerial-category-desc'>{cat.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Nomination Guidelines */}
      <div className='ministerial-guidelines'>
        <div className='ministerial-guidelines-text'>
          Click on the button below to view the Nomination Guidelines for the CANTO Ministerial Awards for ICT Leadership & Digital Transformation.
        </div>
        <a
          href='https://www.flipsnack.com/cantopublications/ministerial-awards-information-package'
          target='_blank'
          rel='noopener noreferrer'
          className='ministerial-guidelines-btn'
        >
          Nomination Guidelines
        </a>
      </div>

      {/* Footer Alert */}
      <div className='ministerial-footer-alert'>
        <div className='ministerial-footer-alert-divider' />
        <div className='ministerial-footer-deadline'>Deadline for Submission of Nominations: June 12th, 2026</div>
        <div className='ministerial-footer-contact'>
          For Additional Information email: <a href='mailto:canto@canto.org'>canto@canto.org</a>
        </div>
        <div className='ministerial-footer-subject'>
          Subject: CANTO Ministerial Innovation Awards
        </div>
      </div>

      {/* About Modal */}
      {showAboutModal && (
        <div className='ministerial-modal-overlay' onClick={() => setShowAboutModal(false)}>
          <div className='ministerial-modal-content' onClick={(e) => e.stopPropagation()}>
            <button className='ministerial-modal-close' onClick={() => setShowAboutModal(false)}>
              ×
            </button>
            <h2 className='ministerial-modal-title'>About the Awards</h2>
            <div className='ministerial-modal-text'>
              <p>
                In the defining era of the digital economy, visionary leadership at the ministerial level has emerged as a decisive force shaping the future of nations. Across the Caribbean, Ministers responsible for Information and Communication Technologies stand at the forefront of transformation—guiding policy, expanding connectivity, and ensuring that digital innovation drives inclusive prosperity, resilience, and sustainable development.
              </p>
              <p>
                As the region's premier association representing the telecommunications and ICT ecosystem, CANTO recognizes that progress in the digital age is driven by bold leadership, strategic vision, and a commitment to collaboration across sectors and borders. It is therefore both fitting and necessary to honour those Ministers whose stewardship is accelerating the digital transformation of their nations and strengthening the Caribbean's collective position in the global digital landscape.
              </p>
              <p>
                The CANTO Ministerial Awards for ICT Leadership & Digital Transformation stands as a distinguished recognition of exceptional ministerial leadership. Conferred on a biennial basis, the awards celebrate those whose actions have advanced digital policy, expanded access to technology, and catalyzed innovation that benefits citizens, industries, and governments alike. Beyond recognition, the awards seek to inspire continued leadership in positioning ICT as a central pillar of economic growth, social inclusion, and regional competitiveness.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
